import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const PHONE_E164 = "+2349059371366";
const PHONE_DISPLAY = "+234 905 937 1366";
const ADDRESS_LINES = ["No 11, Oyelami Street", "Ketu-Ijanikin, Lagos, Nigeria"];

const mapsQuery = encodeURIComponent("No 11 Oyelami Street, Ketu-Ijanikin, Lagos, Nigeria");
const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

function PhoneIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function MapPinIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

export default function ContactPage() {
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [addressRevealed, setAddressRevealed] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-gray-900 font-sans antialiased">
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white text-center py-12 shadow-xl relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-3xl mx-auto px-4"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm underline-offset-4 text-white/85 hover:text-white transition mb-8"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to HunterFortress
          </Link>
          <img
            src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png"
            alt="HunterFortress Logo"
            className="mx-auto h-20 mb-6 drop-shadow-lg"
          />
          <motion.h1
            className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, type: "spring" }}
          >
            Contact
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl font-light max-w-2xl mx-auto opacity-95 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            Reach the team — phone and street address stay hidden until you choose to show them.
          </motion.p>
        </motion.div>
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 rounded-full border-2 border-blue-300/30 pointer-events-none"
          animate={{ scale: [1, 1.35, 1.75], opacity: [0.22, 0.06, 0] }}
          transition={{ repeat: Infinity, duration: 4, ease: "easeOut" }}
          aria-hidden
        />
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 space-y-12">
        <motion.section
          className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-blue-900 mb-3 flex items-center justify-center gap-3">
            <span className="bg-blue-100 p-2 rounded-full">✉️</span>
            Email
          </h2>
          <p className="text-gray-600 mb-4 max-w-lg mx-auto">
            For security consulting, audits, and general questions — we reply as soon as we can.
          </p>
          <a
            href="mailto:security@hunterfortress.com"
            className="inline-flex items-center gap-2 text-blue-700 font-semibold text-lg hover:text-blue-900 transition break-all"
          >
            <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            security@hunterfortress.com
          </a>
        </motion.section>

        <div className="grid md:grid-cols-2 gap-6">
          <motion.section
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden flex flex-col"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
          >
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-bold text-blue-900">Phone</h2>
                  <p className="text-sm text-gray-500 mt-1">Not shown until you tap the icon.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPhoneRevealed((v) => !v)}
                  aria-expanded={phoneRevealed}
                  aria-label={phoneRevealed ? "Hide phone number" : "Show phone number"}
                  className="shrink-0 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-md hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition"
                >
                  <PhoneIcon className="w-7 h-7" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                {!phoneRevealed ? (
                  <motion.div
                    key="hint-phone"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex-1 flex flex-col justify-center min-h-[5rem] rounded-lg bg-blue-50/80 border border-dashed border-blue-200 px-4 py-6 text-center"
                  >
                    <p className="text-gray-600 text-sm">
                      Tap the{" "}
                      <span className="font-medium text-blue-800">phone</span> icon to reveal our number and call link.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="show-phone"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex-1 rounded-lg bg-gray-50 border border-gray-200 px-4 py-5 space-y-3"
                  >
                    <p className="text-2xl font-bold text-blue-900 tracking-tight tabular-nums">{PHONE_DISPLAY}</p>
                    <a
                      href={`tel:${PHONE_E164}`}
                      className="inline-flex items-center justify-center w-full sm:w-auto gap-2 rounded-lg bg-blue-600 text-white font-semibold px-5 py-2.5 hover:bg-blue-700 transition"
                    >
                      <PhoneIcon className="w-5 h-5" />
                      Call now
                    </a>
                    <button
                      type="button"
                      onClick={() => setPhoneRevealed(false)}
                      className="text-sm text-gray-500 hover:text-gray-800 underline underline-offset-2"
                    >
                      Hide number
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden flex flex-col"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.05 }}
          >
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-bold text-blue-900">Address</h2>
                  <p className="text-sm text-gray-500 mt-1">Full street address is revealed on request.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAddressRevealed((v) => !v)}
                  aria-expanded={addressRevealed}
                  aria-label={addressRevealed ? "Hide address" : "Show full address"}
                  className="shrink-0 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-md hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition"
                >
                  <MapPinIcon className="w-7 h-7" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                {!addressRevealed ? (
                  <motion.div
                    key="hint-address"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex-1 flex flex-col justify-center min-h-[5rem] rounded-lg bg-blue-50/80 border border-dashed border-blue-200 px-4 py-6 text-center"
                  >
                    <p className="text-gray-600 text-sm">
                      We are based in <span className="font-medium text-blue-800">Lagos, Nigeria</span>. Tap the{" "}
                      <span className="font-medium text-blue-800">map pin</span> to see the full address.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="show-address"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex-1 rounded-lg bg-gray-50 border border-gray-200 px-4 py-5 space-y-4"
                  >
                    <address className="not-italic text-gray-800 leading-relaxed">
                      {ADDRESS_LINES.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </address>
                    <a
                      href={MAPS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 transition"
                    >
                      <MapPinIcon className="w-5 h-5 shrink-0" />
                      Open in Google Maps
                    </a>
                    <button
                      type="button"
                      onClick={() => setAddressRevealed(false)}
                      className="block text-sm text-gray-500 hover:text-gray-800 underline underline-offset-2"
                    >
                      Hide address
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>
        </div>

        <motion.section
          className="bg-blue-900 text-white p-8 rounded-xl text-center"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <h2 className="text-xl font-bold mb-2">Project inquiry form</h2>
          <p className="text-blue-100/90 text-sm sm:text-base max-w-xl mx-auto mb-5">
            Prefer to outline scope and packages in one go? Use the secure form on our homepage.
          </p>
          <Link
            to="/#contactForm"
            className="inline-flex items-center gap-2 rounded-lg bg-white text-blue-900 font-semibold px-6 py-3 hover:bg-blue-50 transition"
          >
            Go to contact form
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </motion.section>
      </main>

      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <img src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" alt="Logo" className="h-10 mr-3" />
            <span className="text-xl font-bold">HunterFortress</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-blue-300 transition font-semibold">
              Home
            </Link>
            <Link to="/contact" className="hover:text-blue-300 transition font-semibold">
              Contact
            </Link>
            <Link to="/storepal" className="hover:text-blue-300 transition font-semibold">
              StorePal
            </Link>
            <Link to="/terms" className="hover:text-blue-300 transition font-semibold">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-blue-300 transition">
              Privacy
            </Link>
            <Link to="/blog" className="hover:text-blue-300 transition">
              Blog
            </Link>
          </div>
        </div>
        <div className="text-center mt-6 pt-6 border-t border-blue-700">
          <p>&copy; 2025 HunterFortress. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
